import { ritzDataContext, selectLoggedInAgent, selectProjectAgent } from '@moderepo/mode-data-state-ritz';
import { Agent, AgentRole, AlertConditionType, CreateAlertRuleParams } from '@moderepo/mode-internal-apis';
import { modeUIContext, setIsEditingForm } from '@moderepo/mode-ui-state';
import React, { CSSProperties, useCallback, useContext, useEffect, useState } from 'react';
import { EditAlertRuleInfoContainer, ReadonlyAlertRuleInfoContainer } from '.';
import { useSelectProject } from '../../utils/dataStateHooks';



export interface AlertRuleInfoContainerProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;

    readonly projectId: number;

    // targetAlertRule is either an AlertRule object ID (for Edit Alert Rule mode), or an AlertConditionType value (for CREATE Alert Rule mode)
    // or a clone of an alert rule which is similar to AlertRule object but without some of the attributes
    readonly targetAlertRule: number | AlertConditionType | CreateAlertRuleParams;

    // OPTIONAL handler to be notified when the Edit Alert rule is done or cancelled.
    readonly onCancelEdit?: (()=> void) | undefined;
    readonly onFinishEdit?: (()=> void) | undefined;
}



export const AlertRuleInfoContainer: React.FC<AlertRuleInfoContainerProps> = (props: AlertRuleInfoContainerProps) => {

    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const project = useSelectProject(props.projectId);
    // Get the logged in Agent data for the given project. This is is not the same as loggedInAgent data.
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    const projectAgent = loggedInAgent ? selectProjectAgent(dataState, project?.id, loggedInAgent.id) : undefined;
    const canEditAlertRules = projectAgent?.role === AgentRole.ADMIN;
    const [isEditing, setIsEditing] = useState<boolean>();

    // Because props.targetAlertRule can be an ID of an existing Alert Rule or a New Alert Rule type or a clone of an existing alert rule,
    // we need to check to see what it is, existingAlertRuleId or alert rule type or a clone alert rule, before we can use it.
    const existingAlertRuleId = typeof props.targetAlertRule === 'number' ? props.targetAlertRule : undefined;
    const isNewAlertRule = typeof props.targetAlertRule === 'string' || typeof props.targetAlertRule === 'object';


    /**
     * Set the edit Alert Rule state to the specified value. This will update both the local and global state.
     */
    const setEditAlertState = useCallback((editState: boolean) => {
        if (!editState || canEditAlertRules) {
            // Update the isEditing local state
            setIsEditing(editState);

            // Set the isEditingForm global state
            uiDispatch(setIsEditingForm(editState));
        }
    }, [canEditAlertRules, uiDispatch]);


    /**
     * This useEffect will automatically set the Edit Alert Rule state to true on component init if the "alertRuleId" is not provided.
     * If "alertRuleId" is not provided, it means this component is used for CREATING NEW alert rule therefore it always needs to be in Edit mode.
     */
    useEffect(() => {
        if (isNewAlertRule) {
            setEditAlertState(true);
        }
    }, [setEditAlertState, isNewAlertRule]);


    /**
     * on Edit Alert button clicked from the ReadonlyAlertRuleInfoContainer
     */
    const onEditAlertRuleClickHandler = useCallback(() => {
        setEditAlertState(true);
    }, [setEditAlertState]);



    /**
     * Handler for the Cancel Edit event from the EditAlertRuleInfoContainer
     */
    const onCancelEdit = useCallback(() => {
        setEditAlertState(false);

        // Notify the parent container
        if (props.onCancelEdit) {
            props.onCancelEdit();
        }
    }, [setEditAlertState, props]);



    /**
     * Handler for the Finish Edit event from the EditAlertRuleInfoContainer
     */
    const onFinishEdit = useCallback(() => {
        setEditAlertState(false);

        // Notify the parent container
        if (props.onFinishEdit) {
            props.onFinishEdit();
        }
    }, [setEditAlertState, props]);



    return (
        <div className={props.className} style={props.style}>
            {existingAlertRuleId && (!isEditing || !canEditAlertRules) && (
                <ReadonlyAlertRuleInfoContainer
                    className={props.className}
                    style={props.style}
                    projectId={props.projectId}
                    alertRuleId={existingAlertRuleId}
                    canEditAlertRule={canEditAlertRules}
                    onEditAlertRule={onEditAlertRuleClickHandler}
                />
            )}

            {isEditing && canEditAlertRules && (
                <EditAlertRuleInfoContainer
                    className={props.className}
                    style={props.style}
                    projectId={props.projectId}
                    targetAlertRule={props.targetAlertRule}
                    onCancelEdit={onCancelEdit}
                    onFinishEdit={onFinishEdit}
                />
            )}
        </div>
    );
};
